import { useState, useEffect } from "react";
import Head from "next/head";
import Image from "next/image";
import { SectionLayout } from "@/components/SectionLayout";
import { Heading, Text } from "@/components/Text";
import { Button } from "@/components/Button";
import { getAboutUs } from "../../sanity/lib/getCmsData";
import { urlForImage } from "../../sanity/lib/image";

export async function getStaticProps() {
  const data = await getAboutUs();
  return {
    props: {
      data,
    },
    revalidate: 10,
  };
}

export default function About({ data }) {
  const experinceBlockData = data.imageItemText;
  const offeringsBlockData = data.offeringsBlock;
  const leadershipBlockData = data.leadershipBlock;
  const boardBlockData = data.boardBlock;

  useEffect(() => {
    import("aos").then((AOS) => {
      import("aos/dist/aos.css");
      AOS.default.init({ duration: 500, easing: "ease-in", once: true, delay: 25 });
    });
  }, []);
  return (
    <>
      <Head>
        <title>About Us | Red Ball Drills</title>
        <meta
          name="description"
          content="Experior Group Inc. (EGI), headquartered in Tysons Corner, Virginia, was founded in 2007 to provide premium risk management services for domestic and international clients."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Hero data={data} />
      <Experience data={experinceBlockData} />
      <Offerings data={offeringsBlockData} />
      <Leadership data={leadershipBlockData} />
      <Board data={boardBlockData} />
    </>
  );
}

function Hero({ data }) {
  const { name } = data.itemName;
  const { title, description, link } = data.videoArticle;

  const [playVideo, setPlayVideo] = useState(false);

  const handleClick = () => {
    setPlayVideo(true);
  };

  return (
    <>
      <div className="relative lg:px-16">
        <section className="w-full mt-14 px-5 md:mt-16 md:px-8 xl:mt-16">
          <div className="mx-auto max-w-[1170px]">
            <div className="relative">
              <div className="pt-12 pb-14 md:pb-20 px-5 md:px-11 lg:px-0 flex flex-col gap-16">
                <section className="flex flex-col gap-12 lg:gap-0 lg:flex-row">
                  <div className="flex-1">
                    <div className="lg:max-w-[512px] lg:pr-8 ">
                      <Heading
                        as="h1"
                        size="page"
                        className="mb-6"
                        data-aos="fade-in"
                      >
                        {title}
                      </Heading>
                      <Text className="mb-10">{description}</Text>
                      <Button role="contact" />
                    </div>
                  </div>
                  <div className="flex-1 flex">
                    <div className="relative rounded-xl overflow-hidden aspect-video w-full">
                      <iframe
                        width="100%"
                        height="100%"
                        src={link}
                        title={title}
                        allow="autoplay"
                        allowFullScreen
                        loading="lazy"
                      ></iframe>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </section>
      </div>
      <SectionLayout className="!my-12 md:!my-14 xl:!my-16">
        <div
          data-aos="fade-in"
          data-aos-duration="800"
          className="flex flex-col items-center"
        >
          <div className="w-24 h-[2px] bg-rbd-primary mb-6"></div>
          <Text size="highlight">{name}</Text>
          <div className="w-24 h-[2px] bg-rbd-primary mt-6"></div>
        </div>
      </SectionLayout>
    </>
  );
}

function Experience({ data }) {
  const { title, description, mainImage } = data;
  return (
    <SectionLayout>
      <Heading data-aos="fade-in" className="mb-6">
        {title}
      </Heading>
      <article className="flex flex-col md:flex-row gap-10">
        <div className="flex-1">
          <Image
            src={urlForImage(mainImage).url()}
            width={519}
            height={290}
            alt={mainImage.caption}
            className="object-cover w-full aspect-[16/9] md:aspect-[3/2] rounded-xl"
          />
        </div>
        <div className="flex-[1.15_1_0%] lg:flex-1">
          <div className="md:mb-8 hidden md:block" data-aos="fade-in">
            <p className="text-9xl font-oriya font-bold text-rbd-primary leading-none">
              10<sup className="text-5xl align-super">+</sup>
            </p>
          </div>
          <Text>{description}</Text>
        </div>
      </article>
    </SectionLayout>
  );
}

function Offerings({ data }) {
  const { description, title, offerings } = data;

  return (
    <div className="bg-rbd-secondary-base">
      <SectionLayout>
        <div className="py-24 flex flex-col lg:flex-row gap-10 xl:gap-0">
          <article className="xl:w-[45%] shrink-0">
            <div className="max-w-[23.125rem]">
              <Heading className="mb-6" data-aos="fade-in">
                {title}
              </Heading>
              <Text className="mb-10">{description}</Text>
              <Button role="alt-learn" />
            </div>
          </article>
          <section className="mx-auto xl:w-[55%] max-w-2xl flex flex-wrap justify-center gap-8">
            {!!offerings?.length &&
              offerings.map((offering) => {
                return (
                  <article
                    key={offering._key}
                    className="w-48 h-48 p-8 flex items-center justify-center text-center bg-rbd-base rounded-xl shadow-surround-sm"
                  >
                    <Text size="include-card" className="capitalize">
                      {offering.title}
                    </Text>
                  </article>
                );
              })}
          </section>
        </div>
      </SectionLayout>
    </div>
  );
}

function Leadership({ data }) {
  const { title, leaders } = data;
  return (
    <SectionLayout>
      <Heading className="mb-6" data-aos="fade-in">
        {title}
      </Heading>
      <div className="flex flex-col gap-12 overflow-x-hidden lg:overflow-x-visible">
        {leaders.map((leader, index) => (
          <div
            key={index}
            data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}
            data-aos-duration="800"
            className="relative"
          >
            <div
              className={`pb-9 px-5 py-8 md:py-10 ${
                index % 2 === 0 ? "md:pl-12" : "md:pr-12"
              } md:pr-0 flex flex-col gap-16`}
            >
              <section
                className={`z-10 flex gap-9 ${
                  index % 2 === 0
                    ? "flex-col md:flex-row"
                    : "flex-col-reverse md:flex-row-reverse"
                } md:items-center md:gap-10 lg:gap-11`}
              >
                <div
                  className={`md:w-[45%] lg:w-[36%] ${
                    index % 2 !== 0 ? "order-2 md:order-none" : ""
                  }`}
                >
                  <article>
                    <Heading as="h3" size="name" className="mb-[0.125rem]">
                      {leader.title}
                    </Heading>
                    <Heading
                      as="h4"
                      size="title"
                      className="mb-3 text-rbd-tertiary"
                    >
                      {leader.subtitle}
                    </Heading>
                    <Text>{leader.description}</Text>
                  </article>
                </div>
                <div
                  className={`md:w-[55%] ${
                    index % 2 === 0 ? "" : "flex justify-end"
                  }`}
                >
                  <Image
                    quality={100}
                    width={544}
                    height={320}
                    src={urlForImage(leader.mainImage).url()}
                    alt={leader.mainImage.caption}
                    className="mx-auto lg:mx-0 w-full h-full lg:max-h-80 max-w-[34rem] object-cover rounded-xl aspect-[4/3] lg:aspect-auto"
                  />
                </div>
              </section>
            </div>
            <div
              className={`absolute top-0 w-full md:w-4/5 lg:w-[53%] ${
                index % 2 === 0 ? "md:left-0" : "md:right-0"
              } h-[calc(100%-7rem)] md:h-full rounded-xl bg-rbd-secondary-base`}
            ></div>
          </div>
        ))}
      </div>
    </SectionLayout>
  );
}

function Board({ data }) {
  const { title, board } = data;
  return (
    <SectionLayout>
      <Heading className="mb-6" data-aos="fade-in">
        {title}
      </Heading>
      <div className="flex flex-col gap-12 overflow-x-hidden lg:overflow-x-visible">
        {board.map((boardMember, index) => (
          <div
            key={index}
            data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}
            data-aos-duration="800"
            className="relative"
          >
            <div
              className={`pb-9 px-5 py-8 md:py-10 ${
                index % 2 === 0 ? "md:pl-12" : "md:pr-12"
              } md:pr-0 flex flex-col gap-16`}
            >
              <section
                className={`z-10 flex gap-9 ${
                  index % 2 === 0
                    ? "flex-col md:flex-row"
                    : "flex-col-reverse md:flex-row-reverse"
                } md:items-center md:gap-10 lg:gap-11`}
              >
                <div
                  className={`md:w-[45%] lg:w-[36%] ${
                    index % 2 !== 0 ? "order-2 md:order-none" : ""
                  }`}
                >
                  <article>
                    <Heading as="h3" size="name" className="mb-[0.125rem]">
                      {boardMember.title}
                    </Heading>
                    <Heading
                      as="h4"
                      size="title"
                      className="mb-3 text-rbd-tertiary"
                    >
                      {boardMember.subtitle}
                    </Heading>
                    <Text>{boardMember.description}</Text>
                  </article>
                </div>
                <div
                  className={`md:w-[55%] ${
                    index % 2 === 0 ? "" : "flex justify-end"
                  }`}
                >
                  <Image
                    quality={100}
                    width={544}
                    height={320}
                    src={urlForImage(boardMember.mainImage).url()}
                    alt={boardMember.mainImage.caption}
                    className="mx-auto lg:mx-0 w-full h-full lg:max-h-80 max-w-[34rem] object-cover rounded-xl aspect-[4/3] lg:aspect-auto"
                  />
                </div>
              </section>
            </div>
            <div
              className={`absolute top-0 w-full md:w-4/5 lg:w-[53%] ${
                index % 2 === 0 ? "md:left-0" : "md:right-0"
              } h-[calc(100%-7rem)] md:h-full rounded-xl bg-rbd-secondary-base`}
            ></div>
          </div>
        ))}
      </div>
    </SectionLayout>
  );
}
